import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import BusinessHoursEditor from "../../components/BusinessHoursEditor/BusinessHoursEditor";
import { createProfessional } from "../../services/memberService";
import { handleFeatureNotIncluded } from "../../utils/notifyPlanUpgrade";
import api from "../../services/api";
import styles from "./ProfessionalsNew.module.css";
import { ButtonBack, ButtonSave } from "../../components/ui";

const SPECIALTY_OPTIONS = [
  "Cabeleireiro(a)",
  "Manicure / Pedicure",
  "Esteticista",
  "Barbeiro(a)",
  "Maquiador(a)",
  "Massagista",
  "Depilador(a)",
  "Designer de Sobrancelhas",
  "Terapeuta",
  "Outro",
];

const INITIAL_FORM = {
  name: "",
  email: "",
  role: "MEMBER",
  specialty: "",
  bio: "",
  serviceIds: [],
};

export default function ProfessionalsNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [hoursEnabled, setHoursEnabled] = useState(false);
  const [businessHours, setBusinessHours] = useState({});
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [violations, setViolations] = useState([]);

  useEffect(() => {
    api
      .get("/services")
      .then((res) => setServices(res.data.filter((s) => s.isActive)))
      .catch(() => toast.error("Erro ao carregar serviços."))
      .finally(() => setLoadingServices(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (id) => {
    setForm((prev) => {
      const has = prev.serviceIds.includes(id);
      return {
        ...prev,
        serviceIds: has
          ? prev.serviceIds.filter((s) => s !== id)
          : [...prev.serviceIds, id],
      };
    });
  };

  const handleHoursToggle = () => {
    setHoursEnabled((prev) => {
      if (!prev) {
        setBusinessHours({});
      }
      return !prev;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setViolations([]);
    setSubmitting(true);

    const payload = {
      name: form.name,
      email: form.email,
      role: form.role,
    };

    if (form.specialty) payload.specialty = form.specialty;
    if (form.bio) payload.bio = form.bio;
    if (form.serviceIds.length > 0) payload.serviceIds = form.serviceIds;
    if (hoursEnabled) payload.businessHours = businessHours;

    try {
      const res = await createProfessional(payload);
      const { tempPassword } = res.data;
      try {
        await navigator.clipboard.writeText(tempPassword);
        toast.success(
          "Profissional criado! Senha temporária copiada para a área de transferência.",
          {
            autoClose: 8000,
          },
        );
      } catch {
        toast.success(
          `Profissional criado! Senha temporária: ${tempPassword}`,
          {
            autoClose: 8000,
          },
        );
      }
      navigate("/business/professionals");
    } catch (err) {
      // 403 FEATURE_NOT_INCLUDED ≠ 403 LIMIT_REACHED (logo abaixo): um é
      // capacidade fora do tier, o outro é volume estourado. ADR-0026.
      if (handleFeatureNotIncluded(err)) {
        setSubmitting(false);
        return;
      }
      console.log("Status Erro:", err?.response?.status);
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 409) {
        toast.error(data?.error || "E-mail já cadastrado.");
      } else if (status === 403 && data?.code === "LIMIT_REACHED") {
        toast.error("Limite de membros do seu plano atingido.");
      } else if (status === 422 && data?.code === "hours_violation") {
        setViolations(
          data?.violations ?? [data?.error ?? "Expediente inválido."],
        );
      } else {
        toast.error("Erro ao criar profissional. Tente novamente.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Novo Profissional</h1>
          <p className={styles.subtitle}>
            Cadastre um novo membro na sua equipe
          </p>
        </div>
        <ButtonBack to="/business/professionals" />
      </div>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {/* Nome + E-mail */}
        <div className={styles.fieldGroup}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="name">
              Nome <span className={styles.required}>*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              className={styles.input}
              value={form.name}
              onChange={handleChange}
              placeholder="Nome completo"
              required
              minLength={2}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="email">
              E-mail <span className={styles.required}>*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              className={styles.input}
              value={form.email}
              onChange={handleChange}
              placeholder="email@exemplo.com"
              required
            />
          </div>
        </div>

        {/* Função + Especialidade */}
        <div className={styles.fieldGroup}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="role">
              Função
            </label>
            <select
              id="role"
              name="role"
              className={styles.select}
              value={form.role}
              onChange={handleChange}
            >
              <option value="MEMBER">Membro</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="specialty">
              Especialidade
            </label>
            <select
              id="specialty"
              name="specialty"
              className={styles.select}
              value={form.specialty}
              onChange={handleChange}
            >
              <option value="">— Selecione —</option>
              {SPECIALTY_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Bio */}
        <div className={styles.field}>
          <label className={styles.label} htmlFor="bio">
            Bio
          </label>
          <textarea
            id="bio"
            name="bio"
            className={styles.textarea}
            value={form.bio}
            onChange={handleChange}
            placeholder="Breve descrição sobre o profissional..."
            maxLength={500}
            rows={4}
          />
          <span className={styles.charCount}>{form.bio.length}/500</span>
        </div>

        {/* Serviços */}
        <div>
          <p className={styles.sectionLabel}>Serviços</p>
          {loadingServices ? (
            <span className={styles.loadingServices}>
              Carregando serviços...
            </span>
          ) : services.length === 0 ? (
            <span className={styles.loadingServices}>
              Nenhum serviço ativo cadastrado.
            </span>
          ) : (
            <div className={styles.serviceGrid}>
              {services.map((svc) => (
                <label key={svc.id} className={styles.serviceItem}>
                  <input
                    type="checkbox"
                    checked={form.serviceIds.includes(svc.id)}
                    onChange={() => handleServiceToggle(svc.id)}
                  />
                  <div>
                    <div className={styles.serviceName}>{svc.name}</div>
                    <div className={styles.serviceDuration}>
                      {svc.duration} min
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Expediente individual */}
        <div>
          <div className={styles.hoursToggleRow}>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={hoursEnabled}
                onChange={handleHoursToggle}
              />
              <span className={styles.toggleSlider} />
            </label>
            <span className={styles.toggleLabel}>Expediente individual</span>
            <span className={styles.toggleHint}>
              {hoursEnabled
                ? "Horário personalizado para este profissional"
                : "Segue o horário geral do negócio"}
            </span>
          </div>

          {hoursEnabled && (
            <div style={{ marginTop: 16 }}>
              <BusinessHoursEditor
                hours={businessHours}
                onChange={setBusinessHours}
              />
            </div>
          )}
        </div>

        {/* Violations alert */}
        {violations.length > 0 && (
          <div className={styles.violationsList}>
            <p className={styles.violationsTitle}>Expediente inválido</p>
            <ul>
              {violations.map((v, i) => (
                <li key={i}>{v}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className={styles.actions}>
          <ButtonBack
            to="/business/professionals"
            label="Cancelar"
            disabled={submitting}
          />
          <ButtonSave
            type="submit"
            isLoading={submitting}
            loadingText="Criando..."
          >
            Criar profissional
          </ButtonSave>
        </div>
      </form>
    </div>
  );
}
